import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const CategoryDataTable = ({ data, currency, isPro, showInsights }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'amount', direction: 'desc' });

  const sortedData = [...data]?.sort((a, b) => {
    if (sortConfig?.key === 'amount') {
      return sortConfig?.direction === 'asc' ? a?.value - b?.value : b?.value - a?.value;
    }
    if (sortConfig?.key === 'category') {
      return sortConfig?.direction === 'asc' ? a?.name?.localeCompare(b?.name) : b?.name?.localeCompare(a?.name);
    }
    if (sortConfig?.key === 'percentage') {
      return sortConfig?.direction === 'asc' ? a?.percentage - b?.percentage : b?.percentage - a?.percentage;
    }
    return 0;
  });

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig?.key !== columnKey) {
      return <Icon name="ChevronsUpDown" size={16} color="var(--color-muted-foreground)" />;
    }
    return sortConfig?.direction === 'asc' ?
      <Icon name="ChevronUp" size={16} color="var(--color-primary)" /> :
      <Icon name="ChevronDown" size={16} color="var(--color-primary)" />;
  };

  const getTrendIcon = (trend) => {
    if (trend === 'up') return { icon: 'TrendingUp', color: 'var(--color-error)' };
    if (trend === 'down') return { icon: 'TrendingDown', color: 'var(--color-success)' };
    return { icon: 'Minus', color: 'var(--color-muted-foreground)' };
  };

  const getBudgetRiskBadge = (risk) => {
    if (risk === 'high') return { label: 'Over Budget', color: 'bg-error/10 text-error' };
    if (risk === 'medium') return { label: 'Near Limit', color: 'bg-warning/10 text-warning' };
    return null;
  };

  return (
    <div className="bg-card rounded-lg p-16 md:p-20 lg:p-24 shadow-elevation-2">
      <div className="flex items-center justify-between mb-16">
        <div className="flex items-center">
          <Icon name="Table" size={24} color="var(--color-primary)" strokeWidth={2.5} />
          <h3 className="ml-12 text-lg md:text-xl lg:text-2xl font-heading font-semibold text-card-foreground">
            Category Details
          </h3>
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-12 px-16">
                <button
                  onClick={() => handleSort('category')}
                  className="flex items-center gap-8 text-sm font-caption font-semibold text-muted-foreground hover:text-primary transition-smooth min-h-[44px]">
                  Category
                  <SortIcon columnKey="category" />
                </button>
              </th>
              <th className="text-right py-12 px-16">
                <button
                  onClick={() => handleSort('amount')}
                  className="flex items-center justify-end gap-8 text-sm font-caption font-semibold text-muted-foreground hover:text-primary transition-smooth min-h-[44px] ml-auto">
                  Amount
                  <SortIcon columnKey="amount" />
                </button>
              </th>
              <th className="text-right py-12 px-16">
                <button
                  onClick={() => handleSort('percentage')}
                  className="flex items-center justify-end gap-8 text-sm font-caption font-semibold text-muted-foreground hover:text-primary transition-smooth min-h-[44px] ml-auto">
                  Percentage
                  <SortIcon columnKey="percentage" />
                </button>
              </th>
              {isPro && (
                <>
                  <th className="text-right py-12 px-16">
                    <span className="text-sm font-caption font-semibold text-muted-foreground">Change</span>
                  </th>
                  <th className="text-right py-12 px-16">
                    <span className="text-sm font-caption font-semibold text-muted-foreground">Trend</span>
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {sortedData?.map((item, index) => {
              const trendInfo = getTrendIcon(item?.trend);
              const riskBadge = getBudgetRiskBadge(item?.budgetRisk);
              
              return (
                <tr key={index} className="border-b border-border hover:bg-muted transition-smooth">
                  <td className="py-16 px-16">
                    <div className="flex items-center">
                      <div className="w-8 h-8 rounded-full flex-shrink-0" style={{ backgroundColor: item?.color }} />
                      <div className="ml-12">
                        <span className="text-base font-caption font-medium text-card-foreground">
                          {item?.name}
                        </span>
                        {isPro && riskBadge && (
                          <span className={`ml-8 text-xs px-6 py-2 rounded-full ${riskBadge?.color}`}>
                            {riskBadge?.label}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-16 px-16 text-right">
                    <span className="text-base font-heading font-semibold text-card-foreground whitespace-nowrap">
                      {currency}{item?.value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </td>
                  <td className="py-16 px-16 text-right">
                    <span className="text-base font-caption font-medium text-muted-foreground whitespace-nowrap">
                      {item?.percentage?.toFixed(1)}%
                    </span>
                  </td>
                  {isPro && (
                    <>
                      <td className="py-16 px-16 text-right">
                        <span className={`text-sm font-semibold whitespace-nowrap ${
                          item?.change > 0 ? 'text-error' : item?.change < 0 ? 'text-success' : 'text-muted-foreground'
                        }`}>
                          {item?.change > 0 ? '+' : ''}{item?.change?.toFixed(1)}%
                        </span>
                      </td>
                      <td className="py-16 px-16 text-right">
                        <Icon name={trendInfo?.icon} size={16} color={trendInfo?.color} />
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Pro-only: Insights below table */}
        {isPro && showInsights && (
          <div className="mt-24 pt-24 border-t border-border">
            <h4 className="text-sm font-semibold text-foreground mb-12 flex items-center gap-8">
              <Icon name="Lightbulb" size={16} color="var(--color-primary)" />
              Category Insights
            </h4>
            <div className="space-y-8">
              {sortedData?.filter(item => item?.insight)?.slice(0, 5)?.map((item, index) => (
                <div key={index} className="flex items-start gap-8 text-sm">
                  <div className="w-6 h-6 rounded-full flex-shrink-0 mt-2" style={{ backgroundColor: item?.color }} />
                  <div>
                    <span className="font-medium text-foreground">{item?.name}:</span>
                    <span className="text-muted-foreground ml-4">{item?.insight}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-12">
        {/* Sort Controls */}
        <div className="flex items-center gap-8 pb-12 border-b border-border">
          <span className="text-sm text-muted-foreground">Sort by:</span>
          <button
            onClick={() => handleSort('category')}
            className={`px-12 py-6 rounded-lg text-sm font-caption transition-smooth ${
              sortConfig?.key === 'category' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'
            }`}
          >
            Category
          </button>
          <button
            onClick={() => handleSort('amount')}
            className={`px-12 py-6 rounded-lg text-sm font-caption transition-smooth ${
              sortConfig?.key === 'amount' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'
            }`}
          >
            Amount
          </button>
          <button
            onClick={() => handleSort('percentage')}
            className={`px-12 py-6 rounded-lg text-sm font-caption transition-smooth ${
              sortConfig?.key === 'percentage' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'
            }`}
          >
            %
          </button>
        </div>

        {/* Category Cards */}
        {sortedData?.map((item, index) => {
          const trendInfo = getTrendIcon(item?.trend);
          const riskBadge = getBudgetRiskBadge(item?.budgetRisk);
          
          return (
            <div key={index} className="bg-muted rounded-lg p-16">
              <div className="flex items-center justify-between mb-12">
                <div className="flex items-center flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-full flex-shrink-0" style={{ backgroundColor: item?.color }} />
                  <span className="ml-12 text-base font-caption font-semibold text-card-foreground truncate">
                    {item?.name}
                  </span>
                </div>
                {isPro && riskBadge && (
                  <span className={`text-xs px-6 py-2 rounded-full ${riskBadge?.color} ml-8`}>
                    {riskBadge?.label}
                  </span>
                )}
              </div>
              <div className="flex items-center justify-between pt-12 border-t border-border">
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Amount</p>
                  <p className="text-lg font-heading font-semibold text-card-foreground">
                    {currency}{item?.value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground mb-2">Percentage</p>
                  <p className="text-lg font-heading font-semibold text-primary">
                    {item?.percentage?.toFixed(1)}%
                  </p>
                </div>
              </div>
              {isPro && (
                <div className="flex items-center justify-between pt-12 mt-12 border-t border-border">
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Change</p>
                    <p className={`text-sm font-semibold ${
                      item?.change > 0 ? 'text-error' : item?.change < 0 ? 'text-success' : 'text-muted-foreground'
                    }`}>
                      {item?.change > 0 ? '+' : ''}{item?.change?.toFixed(1)}%
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground mb-2">Trend</p>
                    <Icon name={trendInfo?.icon} size={20} color={trendInfo?.color} />
                  </div>
                </div>
              )}
              {isPro && showInsights && item?.insight && (
                <div className="mt-12 pt-12 border-t border-border">
                  <div className="flex items-start gap-8">
                    <Icon name="Lightbulb" size={14} className="mt-2 flex-shrink-0 text-primary" />
                    <p className="text-xs text-muted-foreground">{item?.insight}</p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CategoryDataTable;